import React, { useState, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { authService } from '../services/authService';
import { config } from '../config/environment';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';

/**
 * Page shown when the user tried to sign in but their email is not yet verified.
 * They are not fully signed in; this page explains they must verify and offers a resend link.
 */
const PleaseVerifyEmailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const recaptchaSiteKey = config.services.recaptchaSiteKey;

  const emailFromState = (location.state as { email?: string } | null)?.email ?? '';
  const [email, setEmail] = useState(emailFromState);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const handleResend = async () => {
    if (!email?.trim()) {
      setResendError('Please enter your email address.');
      return;
    }
    if (recaptchaSiteKey && !recaptchaToken) {
      setResendError('Please complete the verification check first.');
      return;
    }
    setResendError(null);
    setResendLoading(true);
    try {
      await authService.resendVerificationEmail(
        email.trim(),
        recaptchaSiteKey ? recaptchaToken! : 'dev-bypass'
      );
      setResendSuccess(true);
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } catch (e: unknown) {
      setResendError(e instanceof Error ? e.message : 'Failed to resend verification email.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <EmailIcon sx={{ fontSize: 56, color: 'warning.main', mb: 1 }} />
          <Typography variant="h5" component="h1" gutterBottom>
            Email not verified
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You need to verify your email before you can access the system. Check your inbox for the
            verification link, or use the button below to resend it.
          </Typography>
        </Box>

        {!emailFromState && (
          <TextField
            fullWidth
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            sx={{ mb: 2 }}
          />
        )}
        {emailFromState && (
          <Alert severity="info" sx={{ mb: 2 }}>
            We'll send the verification link to <strong>{email}</strong>
          </Alert>
        )}

        {resendError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setResendError(null)}>
            {resendError}
          </Alert>
        )}
        {resendSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            We've sent a new verification link. Check your inbox (and spam folder). You can sign in
            after verifying.
          </Alert>
        )}

        {recaptchaSiteKey && !resendSuccess && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={recaptchaSiteKey}
              onChange={(token) => setRecaptchaToken(token)}
              onExpired={() => setRecaptchaToken(null)}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={handleResend}
            disabled={resendLoading || (!!recaptchaSiteKey && !recaptchaToken && !resendSuccess)}
          >
            {resendLoading ? <CircularProgress size={24} /> : 'Resend verification email'}
          </Button>
          <Button component={Link} to="/login" variant="outlined">
            Back to sign in
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default PleaseVerifyEmailPage;
